// AGENT MODE — the workspace TUI as the whole shell (WS-AGENT-MODE-1).
//
// Nika 2026-10-23: "we have workspace and tui. now we need to add workspace
// agent-mode which is exact replication of TUI in real workspace ui". A real
// DOMQL replica of the TUI frame (smbls cli/tui shell/frame.js — the Mock
// panel of symbols.app/apps): every row, card and field is a real element,
// in brand tokens and the brand's mono face (`Code`):
//
//   ╭──────────────────────────────────────────────────────────────╮
//   │ org.workspace  Tasks  Board  Mine  All               status ⏻ │  header
//   ├────────────────┬─────────────────────────────────────────────┤
//   │ WORKSPACE      │                                             │
//   │ ⌂ Home       1 │   the view: board, list, detail, dashboard, │  sidebar │ content
//   │ ▦ Tasks      2 │   chat or Ask AI                            │
//   ├────────────────┴─────────────────────────────────────────────┤
//   │ ASK AI  Plan next week…                     ? help  q quit    │  footer
//   ╰──────────────────────────────────────────────────────────────╯
//
// THE FRAME DRAWS THE SNAPSHOT AND NOTHING ELSE. What shows, what is
// selected, what a key does: all of it is the shared runtime's
// (`@symbo.ls/workspace-model`, functions/agentModeRuntime.js). Every factory
// below reads `agentModeSnapshot(s.root)` (through functions/agentModeView.js),
// which reads the root's `_agentModeRev` first — one runtime render re-runs
// the factories that read it.
//
// WHOLE SHELL. The frame is fixed over the whole viewport, opaque, above the
// shell's chrome and sheets (the header, the rail, the home stay mounted under
// it and take no keys: the frame's keydown stops every key it hands the
// runtime). It is a modal surface to assistive tech (`aria-modal`).
//
// AGENT-READABLE. Regions carry `data-agent-region`, apps `data-agent-app`,
// views `data-agent-view`, records `data-agent-id`; the tabs are a `tablist`,
// the sidebar and lists are `listbox`es whose cursor row is
// `aria-selected="true"`, overlays are `dialog`s.
//
// The leave control and `q` leave Agent mode (functions/agentMode.js); the
// mount is app.js (`AgentModeFrame`, gated on agentModeShown).

export const AgentModeFrame = {
  tag: 'section',
  role: 'dialog',
  aria: {
    modal: 'true',
    label: (el) => el.call('polyglot', 'workspace.agentMode.frameLabel')
  },
  data: { agentRegion: 'frame' },
  tabindex: '-1',
  position: 'fixed',
  inset: '0',
  zIndex: '1900',
  display: 'flex',
  flexDirection: 'column',
  padding: 'Z',
  boxSizing: 'border-box',
  background: 'document',
  color: 'paragraph',
  fontFamily: 'Code',
  fontSize: 'Z1',
  lineHeight: '1.5',
  outline: 'none',
  // The one keydown bridge (functions/agentModeRuntime.js agentModeKeydown).
  onKeydown: (e, el) => el.call('agentModeKeydown', e),
  // A key no element holds (the focus fell to the page body: the boot
  // screen's inert lock, a removed field) is the frame's too.
  onDocumentKeydown: (e, el) => el.call('agentModeDocumentKeydown', e),
  // The runtime follows Agent mode, the workspace and the language: started
  // at mount, re-created on a workspace switch, told a new language, stopped
  // on the way out.
  onRender: (el) => {
    el.call('agentModeRuntimeEnsure')
    el.call('agentModeFocusFrame', el)
  },
  stateDeps: [
    (el, s) => String((s.root.workspace && (s.root.workspace.id || s.root.workspace._id)) || ''),
    // the list entry becomes the full record: the runtime starts then
    (el, s) => String((s.root.workspace && s.root.workspace._shape) || ''),
    (el, s) => s.root.agentMode === true,
    (el, s) => s.root._agentModeLink === true,
    (el, s) => Number(s.root._agentModeRev) || 0,
    // a language switch: the runtime follows it (agentModeRuntimeEnsure)
    (el, s) => String(s.root.lang || '')
  ],
  onStateUpdate: (el) => {
    el.call('agentModeRuntimeEnsure')
    el.call('agentModeFocusFrame', el)
  },
  onBeforeRemove: (el) => el.call('agentModeRuntimeStop'),

  AgentModeBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minHeight: '0',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'line',
    round: 'radiusCard',
    overflow: 'hidden',

    AgentModeHeader: {},

    AgentModeBody: {
      display: 'flex',
      flex: '1',
      minHeight: '0',
      borderTopWidth: 'hairline',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      borderBottomWidth: 'hairline',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      AgentModeSidebar: {},
      AgentModeContent: {}
    },

    AgentModeFooter: {}
  },

  AgentModeOverlay: {},
  AgentModeStartError: {}
}

// The header: host label, app title, view tabs; the status and the leave
// control on the right (frame.js "Header: host, title, view tabs").
export const AgentModeHeader = {
  tag: 'header',
  data: { agentRegion: 'header' },
  display: 'flex',
  alignItems: 'center',
  gap: 'A',
  minWidth: '0',
  padding: 'Y A',

  AgentModeHost: {
    color: 'caption',
    whiteSpace: 'nowrap',
    flexShrink: '0',
    text: (el, s) => el.call('agentModeHeaderOf', el.call('agentModeSnapshot', s.root)).host
  },
  AgentModeTitle: {
    tag: 'h1',
    margin: '0',
    fontSize: 'inherit',
    fontWeight: '700',
    color: 'title',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
    text: (el, s) => el.call('agentModeHeaderOf', el.call('agentModeSnapshot', s.root)).title
  },
  AgentModeTabs: {
    role: 'tablist',
    data: { agentRegion: 'tabs' },
    aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.views') },
    display: 'flex',
    gap: 'A',
    minWidth: '0',
    overflow: 'hidden',
    childExtends: 'AgentModeTab',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeHeaderOf', el.call('agentModeSnapshot', s.root)).tabs
  },
  AgentModeStatus: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    flexShrink: '0',
    role: 'status',
    data: { agentRegion: 'status' },
    color: (el, s) => el.call('agentModeHeaderOf', el.call('agentModeSnapshot', s.root)).statusInk,
    // A spinner while the runtime works (status kind 'busy'), else its text.
    text: (el, s) => {
      const h = el.call('agentModeHeaderOf', el.call('agentModeSnapshot', s.root))
      return h.busy ? '…' : h.statusText
    }
  },
  // the workspace or its CLI — the same switch as the home's (Nika
  // 2026-10-23: "add switcher so we can switch between normal and CLI-like
  // workspace easily"); `q` and Ctrl+` still leave
  WorkspaceModeSwitch: {}
}

export const AgentModeTab = {
  tag: 'button',
  type: 'button',
  role: 'tab',
  data: { agentTab: (el, s) => s.tab },
  aria: { selected: (el, s) => (s.active ? 'true' : 'false') },
  tabindex: '-1',
  background: 'transparent',
  border: 'none',
  padding: '0',
  font: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  color: (el, s) => (s.active ? 'title' : 'caption'),
  fontWeight: (el, s) => (s.active ? '700' : '400'),
  ':hover': { color: 'title' },
  ':focus-visible': { outline: 'hairline2 solid accentInk' },
  text: (el, s) => s.label,
  onClick: (e, el, s) => el.call('agentModeClick', s.click, { double: e.detail > 1 })
}

// Leave Agent mode — the visible control (`q` does the same).
export const AgentModeLeave = {
  tag: 'button',
  type: 'button',
  data: { agentAction: 'leave' },
  aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.leave') },
  title: (el) => el.call('polyglot', 'workspace.agentMode.leave'),
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'Y',
  flexShrink: '0',
  background: 'transparent',
  border: 'none',
  padding: 'X Y',
  round: 'radiusControl',
  font: 'inherit',
  color: 'caption',
  cursor: 'pointer',
  ':hover': { color: 'title', background: 'hover' },
  ':active': { background: 'selected' },
  ':focus-visible': { outline: 'hairline2 solid accentInk' },
  Icon: { name: 'terminal', boxSize: 'iconSm' },
  AgentModeLeaveText: {
    tag: 'span',
    text: (el) => el.call('polyglot', 'workspace.agentMode.leaveShort')
  },
  onClick: (e, el) => {
    e.stopPropagation()
    el.call('setAgentMode', false)
  }
}

// The sidebar: the workspace, then the numbered apps (frame.js "Sidebar").
export const AgentModeSidebar = {
  tag: 'nav',
  data: { agentRegion: 'sidebar' },
  aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.apps') },
  display: 'flex',
  flexDirection: 'column',
  width: 'F1',
  flexShrink: '0',
  overflowY: 'auto',
  padding: 'Z 0',
  borderRightWidth: 'hairline',
  borderRightStyle: 'solid',
  borderRightColor: 'line',
  '@mobileL': { display: 'none' },

  AgentModeSideList: {
    role: 'listbox',
    aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.apps') },
    display: 'flex',
    flexDirection: 'column',
    childExtends: 'AgentModeSideRow',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeSidebarRowsOf', el.call('agentModeSnapshot', s.root))
  }
}

export const AgentModeSideRow = {
  tag: 'div',
  role: (el, s) => (s.kind === 'item' ? 'option' : 'presentation'),
  data: { agentApp: (el, s) => (s.kind === 'item' ? s.app : null) },
  aria: {
    selected: (el, s) => (s.kind === 'item' ? (s.cursor || s.active ? 'true' : 'false') : null),
    current: (el, s) => (s.kind === 'item' && s.active ? 'page' : null)
  },
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  minWidth: '0',
  padding: (el, s) => (s.kind === 'item' ? 'X A' : s.gap ? 'A A X' : 'X A Z'),
  cursor: (el, s) => (s.kind === 'item' ? 'pointer' : 'default'),
  background: (el, s) => (s.cursor ? 'selected' : 'transparent'),
  color: (el, s) => (s.kind === 'label' ? 'caption' : s.cursor || s.active ? 'title' : 'caption'),
  textTransform: (el, s) => (s.kind === 'label' ? 'uppercase' : 'none'),
  fontWeight: (el, s) => (s.kind === 'item' && s.active ? '700' : '400'),
  ':hover': { background: 'hover' },
  // The app's house glyph — the one the navbar and the App strip wear.
  Icon: {
    aria: { hidden: 'true' },
    boxSize: 'iconSm',
    flexShrink: '0',
    // A row's kind never changes under its key (the key names the kind).
    if: (el, s) => s.kind === 'item',
    color: (el, s) => (s.active ? 'accentInk' : 'caption'),
    name: (el, s) => el.call('glyphNameFor', { route: s.route, name: s.text }, 'ln')
  },
  AgentModeSideLabel: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.text
  },
  AgentModeSideBadge: {
    tag: 'span',
    aria: { hidden: 'true' },
    color: 'accentInk',
    visibility: (el, s) => (s.badge ? 'visible' : 'hidden'),
    text: '•'
  },
  AgentModeSideNumber: {
    tag: 'span',
    aria: { hidden: 'true' },
    color: 'caption',
    hide: (el, s) => s.kind !== 'item' || !s.number,
    text: (el, s) => s.number
  },
  onClick: (e, el, s) => {
    if (s.kind !== 'item') return
    el.call('agentModeClick', s.click, { double: e.detail > 1 })
  }
}

// The footer: ASK AI, its field, the key hints (frame.js "Footer").
export const AgentModeFooter = {
  tag: 'footer',
  data: { agentRegion: 'footer' },
  display: 'flex',
  alignItems: 'center',
  gap: 'A',
  minWidth: '0',
  padding: 'Y A',

  AgentModeAskLabel: {
    tag: 'label',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    fontWeight: (el, s) =>
      el.call('agentModeFooterOf', el.call('agentModeSnapshot', s.root)).focused ? '700' : '400',
    color: (el, s) =>
      el.call('agentModeFooterOf', el.call('agentModeSnapshot', s.root)).focused
        ? 'accentInk'
        : 'caption',
    text: (el) => el.call('polyglot', 'workspace.agentMode.askAi'),
    onClick: (e, el, s) =>
      el.call(
        'agentModeClick',
        el.call('agentModeFooterOf', el.call('agentModeSnapshot', s.root)).click,
        { double: false }
      )
  },
  // One field, as a one-row list: the row's state follows the snapshot.
  AgentModeAsk: {
    flex: '1',
    minWidth: '0',
    display: 'flex',
    childExtends: 'AgentModeField',
    childrenAs: 'state',
    children: (el, s) => [el.call('agentModeFooterOf', el.call('agentModeSnapshot', s.root)).field]
  },
  AgentModeHints: {
    display: 'flex',
    gap: 'A',
    flexShrink: '0',
    whiteSpace: 'nowrap',
    '@mobileL': { display: 'none' },
    childExtends: 'AgentModeHint',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeFooterOf', el.call('agentModeSnapshot', s.root)).hints
  }
}

export const AgentModeHint = {
  tag: 'span',
  AgentModeHintKey: { tag: 'kbd', font: 'inherit', color: 'caption', text: (el, s) => s.k },
  AgentModeHintText: { tag: 'span', color: 'caption', text: (el, s) => ` ${s.v}` }
}

// One text field of the frame: the ASK AI bar, a filter, the chat composer,
// the palette query, a form field. The state carries { target, value,
// placeholder, focused }. Typing is native; every change reaches the model
// (`runtime.input(target, value)`), which stays the source of truth. The
// field takes the browser focus when the model's focus is here.
export const AgentModeField = {
  tag: 'input',
  type: 'text',
  autocomplete: 'off',
  spellcheck: 'false',
  data: { agentTarget: (el, s) => s.target },
  flex: '1',
  minWidth: '0',
  background: 'transparent',
  border: 'none',
  padding: '0',
  font: 'inherit',
  color: 'title',
  outline: 'none',
  '::placeholder': { color: 'caption' },
  aria: {
    label: (el, s) =>
      s.labelKey ? el.call('polyglot', s.labelKey) : s.labelText || s.placeholder || null
  },
  placeholder: (el, s) => s.placeholder,
  value: (el, s) => s.value,
  onInput: (e, el, s) => {
    if (el.scope) el.scope.agentModeCaret = e.target.selectionStart
    el.call('agentModeInput', s.target, e.target.value, e.target.selectionStart)
  },
  // A pointer into a field the model has not focused moves the model's focus
  // there first (the zone the TUI would hit: ask, write, a form field).
  onFocus: (e, el, s) => {
    if (!s.focused && s.focusPayload) el.call('agentModeClick', s.focusPayload, { double: false })
  },
  onRender: (el, s) => el.call('agentModeFocusField', el, s),
  stateDeps: [(el, s) => !!s.focused, (el, s) => Number(s.root._agentModeRev) || 0],
  onStateUpdate: (el, s) => el.call('agentModeFocusField', el, s)
}

// The model could not start (the package failed to load, the session is not
// ready). Says so, keeps the leave control in reach.
export const AgentModeStartError = {
  role: 'alert',
  position: 'absolute',
  inset: 'auto 0 0 0',
  margin: 'auto',
  padding: 'B',
  hide: (el, s) => !s.root._agentModeError,
  color: 'dangerInk',
  textAlign: 'center',
  text: (el) => el.call('polyglot', 'workspace.agentMode.startFailed')
}
